import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { ApiFehler } from "./api/client";
import { Knopf, Spinner } from "./komponenten/Basis";
import { FehlerGrenze } from "./komponenten/FehlerGrenze";
import { Layout } from "./komponenten/Layout";
import { PortalLayout } from "./komponenten/PortalLayout";
import { AuthProvider, istInstallateur, useAuth } from "./kontext/AuthKontext";
import { ToastProvider, useToast } from "./kontext/ToastKontext";
import { internAdresse } from "./lib/bereich";
import { AnlagenSeite } from "./seiten/AnlagenSeite";
import { BetriebSeite } from "./seiten/BetriebSeite";
import { BetriebeSeite } from "./seiten/BetriebeSeite";
import { EinladungSeite } from "./seiten/EinladungSeite";
import { FormularSeite } from "./seiten/formular/FormularSeite";
import { LoginSeite, NichtGefundenSeite } from "./seiten/LoginSeite";
import { MastrSeite } from "./seiten/MastrSeite";
import { AnmeldungSeite } from "./seiten/portal/AnmeldungSeite";
import { NeueAnmeldungSeite } from "./seiten/portal/NeueAnmeldungSeite";
import { PortalStartSeite } from "./seiten/portal/PortalStartSeite";
import { PruefSeite } from "./seiten/PruefSeite";
import { StatusSeite } from "./seiten/status/StatusSeite";
import { UploadSeite } from "./seiten/UploadSeite";

function wiederholen(anzahl: number, fehler: Error): boolean {
  // 4xx nicht wiederholen, Netzwerk-/Serverfehler bis zu 2×
  if (fehler instanceof ApiFehler && fehler.status >= 400 && fehler.status < 500) return false;
  return anzahl < 2;
}

/** QueryClient mit globaler Fehlerbehandlung: jeder API-Fehler erzeugt sofort einen Toast. */
function DatenProvider({ children }: { children: ReactNode }) {
  const toast = useToast();
  const [client] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (fehler, abfrage) => {
            if (fehler instanceof ApiFehler && fehler.status === 401) return;
            // Nur Fehler bei bereits geladenen Daten melden. Erstladefehler zeigt die Seite selbst.
            if (abfrage.state.data !== undefined) toast.fehler(fehler, "Aktualisierung fehlgeschlagen");
            else if (!(fehler instanceof ApiFehler && fehler.status === 404)) toast.fehler(fehler, "Laden fehlgeschlagen");
          },
        }),
        mutationCache: new MutationCache({
          onError: (fehler, _v, _k, mutation) => {
            // meta.eigeneFehlermeldung: Die Komponente zeigt einen passenderen Toast selbst.
            if (mutation.meta?.eigeneFehlermeldung || (fehler instanceof ApiFehler && fehler.status === 401)) return;
            toast.fehler(fehler);
          },
        }),
        defaultOptions: {
          queries: { retry: wiederholen, staleTime: 15_000, refetchOnWindowFocus: true },
          mutations: { retry: false },
        },
      }),
  );
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}

/**
 * Anmeldung erzwingen und nach Rolle trennen: Installateure nur ins Portal, das Team nur ins Backoffice.
 * Die eigentliche Absicherung macht die API (403) – das hier ist die passende Navigation dazu.
 */
function Geschuetzt({ bereich, children }: { bereich: "backoffice" | "portal"; children: ReactNode }) {
  const { benutzer, pruefeSitzung } = useAuth();
  const ort = useLocation();
  if (pruefeSitzung) return <div className="flex min-h-screen items-center justify-center"><Spinner label="Sitzung wird geprüft…" /></div>;
  if (!benutzer) return <Navigate to="/login" replace state={{ von: ort.pathname + ort.search }} />;
  const portal = istInstallateur(benutzer);
  if (bereich === "backoffice" && portal) return <Navigate to="/portal" replace />;
  if (bereich === "portal" && !portal) return <Navigate to="/" replace />;
  // Team-Konto auf portal./status.: Die Backoffice-API ist hier gesperrt, also gleich nach intern. schicken.
  const intern = bereich === "backoffice" ? internAdresse() : null;
  if (intern) return <NurIntern adresse={intern} />;
  return <>{children}</>;
}

function NurIntern({ adresse }: { adresse: string }) {
  const { abmelden } = useAuth();
  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-100 px-4">
      <div className="w-full max-w-sm rounded-2xl border border-neutral-200 bg-white p-8 shadow-karte">
        <img src="/logo.png" alt="AnmeldeMeister" className="h-6 w-auto" />
        <h1 className="mt-6 text-xl font-semibold">Backoffice unter intern.</h1>
        <p className="mt-2 text-sm text-neutral-600">
          Dieses Portal ist für Installateure. Das Backoffice erreichst du nur unter{" "}
          <span className="font-semibold">{new URL(adresse).host}</span> (freigegebene Internetanschlüsse).
        </p>
        <div className="mt-6 space-y-3">
          <a href={adresse} className="block w-full rounded-lg bg-marke px-4 py-2 text-center text-sm font-semibold text-white">
            Zum Backoffice
          </a>
          <Knopf variante="sekundaer" className="w-full" onClick={abmelden}>Abmelden</Knopf>
        </div>
      </div>
    </div>
  );
}

export function App() {
  return (
    <FehlerGrenze>
      <ToastProvider>
        <DatenProvider>
          <AuthProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<LoginSeite />} />
                <Route path="/einladung" element={<EinladungSeite />} />
                <Route path="/status" element={<StatusSeite />} />
                <Route path="/formular" element={<FormularSeite />} />
                <Route
                  path="/portal"
                  element={
                    <Geschuetzt bereich="portal">
                      <PortalLayout />
                    </Geschuetzt>
                  }
                >
                  <Route index element={<PortalStartSeite />} />
                  <Route path="neu" element={<NeueAnmeldungSeite />} />
                  <Route path="anmeldung/:id" element={<AnmeldungSeite />} />
                  <Route path="*" element={<NichtGefundenSeite />} />
                </Route>
                <Route
                  element={
                    <Geschuetzt bereich="backoffice">
                      <Layout />
                    </Geschuetzt>
                  }
                >
                  <Route index element={<UploadSeite />} />
                  <Route path="anlagen" element={<AnlagenSeite />} />
                  <Route path="anlagen/:id" element={<PruefSeite />} />
                  <Route path="anlagen/:id/mastr" element={<MastrSeite />} />
                  <Route path="betriebe" element={<BetriebeSeite />} />
                  <Route path="betriebe/:id" element={<BetriebSeite />} />
                  <Route path="*" element={<NichtGefundenSeite />} />
                </Route>
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </DatenProvider>
      </ToastProvider>
    </FehlerGrenze>
  );
}
